<template>
  <div class="document">
    <div class="header">
      <EsHeader>
        <template #header>
          <div class="header-title">
            Matching &amp; Assignment Rules
          </div>
          <div class="header-subtitle">
            Applies when the Matching Engine performs a match request.
          </div>
        </template>
      </EsHeader>
      <div class="icon-right">
        <button
          class="button button-link"
          :class="{ disabled: editOtherPanels || initialRules.length === 0 }"
          @click="editPanel()"
          v-if="!editCurrentPanel&&hasModule('SYS:SYS:RULEMGR:EDIT')"
          :disabled="editOtherPanels || initialRules.length === 0"
        >
          <font-awesome-icon :icon="['far', 'edit']" size="lg" title="Edit" />
        </button>
      </div>
    </div>
    <div class="content" style="padding : 0.5rem !important">
      <es-loader
        v-if="initialRules.length === 0
          || loading"
        style="margin: 6.5rem"
      />
      <es-exception
        v-else-if="loadingError"
        exceptionMessage="There was an issue.
          Please try again or contact your administrator."
      />
      <table
        v-else
        v-for="eachPersonType in sortedAvailablePersonTypes"
        :key="eachPersonType.id"
       >
        <tbody>
          <tr>
            <th class="category" colspan="2" scope="colgroup">
              {{ eachPersonType.name }} Match Rules
            </th>
          </tr>
          <tr>
            <th class="rule" scope="col" title="Rule Name">
              Rule Name
            </th>
            <th class="rule" scope="col" title="Rule Value(s)">
              Rule Value(s)
            </th>
          </tr>
          <tr
            v-for="eachRule in initialRules.filter(e => e.personType === eachPersonType.code)"
            :key="eachRule.id"
          >
            <th class="th-normal" scope="row">
              <span>
                {{ eachRule.description ? eachRule.description : eachRule.name }}
              </span>
              <span
                v-if="editCurrentPanel
                  && eachRule.name === 'VALUE_FORCE_NMATCH_AGE_DIFF'"
                class="label-optional has-text-grey-light"
              >
                (optional)
              </span>
            </th>
            <td>
              <div v-if="!isNaN(eachRule.value)" class="control">
                <input
                  class="input"
                  :class="{
                    'is-static': !editCurrentPanel,
                    'input-box-width': editCurrentPanel,
                    'is-danger': eachRule.hasErr
                  }"
                  @keypress="isWholeNumbers($event)"
                  @keyup.prevent=" validateField(
                    initialRules.filter(e => e.personType === eachPersonType.code), eachRule
                  )"
                  onPaste="return false"
                  :readonly="!editCurrentPanel"
                  type="text"
                  v-model="eachRule.value"
                  :aria-label="eachRule.value || 'Rule Value'"
                >
                <span v-if="eachRule.hasErr" class="help is-danger">
                  {{ eachRule.hasErrMsg }}
                </span>
              </div>
              <div v-else class="control">
                <div
                  class="buttons has-addons"
                  v-if="editCurrentPanel"
                >
                  <button
                    class="button button--form"
                    v-for="(option, index) in enabledDisabled"
                    :key="index"
                    @click="eachRule.value = option.code"
                    :class="{ 'is-active': eachRule.value === option.code }"
                    style="width: 100px"
                  >
                    {{ option.description }}
                  </button>
                </div>
                <input
                  v-else
                  class="input"
                  :class="{ 'is-static': !editCurrentPanel }"
                  type="text"
                  :value="eachRule.value === 'YES' ? 'Enabled' : 'Disabled'"
                  :readonly="!editCurrentPanel"
                  :aria-label="eachRule.value === 'YES' ? 'Enabled' : 'Disabled'"
                >
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <div
      v-if="editCurrentPanel && !loading && !loadingError"
      class="is-flex flex-justify--end flex-align--center"
      style="margin-right: 5.15rem; padding-bottom: 2rem"
    >
      <button
        class="button button-link has-underline"
        @click.prevent="cancelPanel()"
        style="margin-right: 1rem"
      >
        Cancel
      </button>
      <button
        class="button is-primary"
        @click.prevent="updateRules()"
      >
        Update
      </button>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { mapGetters } from 'vuex';
import { library } from '@fortawesome/fontawesome-svg-core';
import {
  faEdit,
} from '@fortawesome/pro-regular-svg-icons';
import EsException from '@/components/EsException.vue';
import EsHeader from '@/components/EsHeader.vue';
import EsLoader from '@/components/EsLoader.vue';
import NotificationManager from '@/mixins/NotificationManager';
import PermissionManager from '@/mixins/PermissionManager';

library.add(
  faEdit,
);

export default {
  mixins: [
    NotificationManager,
    PermissionManager,
  ],
  components: {
    EsException,
    EsHeader,
    EsLoader,
  },
  props: {
    editOtherPanels: {
      type: Boolean,
    },
    matchingRules: {
      type: Array,
    },
    sortedAvailablePersonTypes: {
      type: Array,
    },
  },
  created() {
    this.enabledDisabled = [
      {
        code: 'YES',
        description: 'Enabled',
      },
      {
        code: 'NO',
        description: 'Disabled',
      },
    ];
  },
  data() {
    return this.initialData();
  },
  watch: {
    matchingRules() {
      this.initialRules = (this.matchingRules.flat()).slice().sort(this.compare);
    },
  },
  computed: {
    ...mapGetters('propertiesModule', [
      'properties',
    ]),
    payLoad() {
      return this.initialRules.filter(
        item1 => !this.cachedRules.some(
          item2 => (
            item1.value === item2.value
            && item1.id === item2.id
          ),
        ),
      );
    },
  },
  methods: {
    cacheValues() {
      this.cachedRules = JSON.parse(
        JSON.stringify(
          (this.matchingRules.flat())
            .slice().sort(this.compare),
        ),
      );
    },
    cancelPanel() {
      this.resetData();
      this.editCurrentPanel = false;
      this.$emit('allowEditOtherPanels');
      this.hideNotification();
    },
    compare(a, b) {
      if (a.description.toUpperCase() < b.description.toUpperCase()) {
        return -1;
      }
      if (a.description.toUpperCase() > b.description.toUpperCase()) {
        return 1;
      }
      return 0;
    },
    editPanel() {
      this.cacheValues();
      this.editCurrentPanel = true;
      this.$emit('allowEditOtherPanels');
    },
    initialData() {
      return {
        cachedRules: [],
        editCurrentPanel: false,
        loading: false,
        loadingError: false,
        initialRules: [],
      };
    },
    isWholeNumbers(evt) {
      // Listen to the key event when a keyboard key is pressed
      evt = (evt) || window.event; // eslint-disable-line
      // Store the ASCII value of the pressed key in 'charCode'
      const charCode = (evt.which) ? evt.which : evt.keyCode;
      // Prevent user from entering any values except digits
      if (charCode > 31 && (charCode < 48 || charCode > 57)) {
        evt.preventDefault();
      }
    },
    resetData() {
      this.initialRules = this.cachedRules;
    },
    async updateRules() {
      if (this.payLoad.find(x => x.hasErr === true)) {
        this.showNotification('danger', ['There were some errors. Please fix the errors highlighted in the fields below.']);
        return;
      }
      if (this.payLoad.length === 0) {
        this.hideNotification();
        this.editCurrentPanel = false;
        this.$emit('allowEditOtherPanels');
      } else {
        try {
          this.hideNotification();
          this.loading = true;
          this.loadingError = false;
          this.payLoad.map((item) => {
            const newItem = item;
            delete newItem.hasErr;
            delete newItem.hasErrMsg;
            return newItem;
          });
          await axios({
            method: 'put',
            url: '/rule-manager/person-id/matching-engine-rules',
            data: this.payLoad,
          });
        } catch (err) {
          this.loadingError = true;
        } finally {
          this.editCurrentPanel = false;
          this.$emit('allowEditOtherPanels');
          this.showNotification('success', ['The \'Matching and Assign Rules\' have been updated successfully.']);
          this.loading = false;
        }
      }
    },
    /* eslint-disable no-param-reassign */
    validateField(allRules, rule) {
      if (rule.name === 'VALUE_FORCE_NMATCH_AGE_DIFF') {
        if (rule.value === '') {
          rule.value = null;
        }
      }
      if (rule.name === 'VALUE_NMATCH_LOWER') {
        const tempUp = allRules.find(x => x.name === 'VALUE_NMATCH_UPPER');
        const low = parseInt(rule.value, 10);
        const high = parseInt(tempUp.value, 10);
        if (Number.isNaN(low)) {
          if (Number.isNaN(high)) {
            rule.hasErr = true;
            rule.hasErrMsg = 'Lower Near Match Threshold is required.';
            tempUp.hasErr = true;
            tempUp.hasErrMsg = 'Upper Near Match Threshold is required.';
          } else if (high === 0) {
            rule.hasErr = true;
            rule.hasErrMsg = 'Lower Near Match Threshold is required.';
            tempUp.hasErr = true;
            tempUp.hasErrMsg = 'Upper Threshold must be between 1 and 100.';
          } else {
            rule.hasErr = true;
            rule.hasErrMsg = 'Lower Near Match Threshold is required.';
            tempUp.hasErr = false;
            tempUp.hasErrMsg = null;
          }
        } else if (low === 0 || low > 100) {
          if (Number.isNaN(high)) {
            rule.hasErr = true;
            rule.hasErrMsg = 'Lower Threshold must be between 1 and 100.';
            tempUp.hasErr = true;
            tempUp.hasErrMsg = 'Upper Near Match Threshold is required.';
          } else if (high === 0) {
            rule.hasErr = true;
            rule.hasErrMsg = 'Lower Threshold must be between 1 and 100.';
            tempUp.hasErr = true;
            tempUp.hasErrMsg = 'Upper Threshold must be between 1 and 100.';
          } else {
            rule.hasErr = true;
            rule.hasErrMsg = 'Lower Threshold must be between 1 and 100.';
            tempUp.hasErr = false;
            tempUp.hasErrMsg = null;
          }
        } else if (low >= high) {
          rule.hasErr = true;
          rule.hasErrMsg = 'Lower Threshold must be less than Upper Threshold.';
          tempUp.hasErr = true;
          tempUp.hasErrMsg = 'Upper Threshold must be more than Lower Threshold.';
        } else if (!Number.isNaN(low) && Number.isNaN(high)) {
          rule.hasErr = false;
          rule.hasErrMsg = null;
          tempUp.hasErr = true;
          tempUp.hasErrMsg = 'Upper Near Match Threshold is required.';
        } else {
          rule.hasErr = false;
          rule.hasErrMsg = null;
          tempUp.hasErr = false;
          tempUp.hasErrMsg = null;
        }
      }
      if (rule.name === 'VALUE_NMATCH_UPPER') {
        const tempLow = allRules.find(x => x.name === 'VALUE_NMATCH_LOWER');
        const low = parseInt(tempLow.value, 10);
        const high = parseInt(rule.value, 10);
        if (Number.isNaN(high)) {
          if (Number.isNaN(low)) {
            rule.hasErr = true;
            rule.hasErrMsg = 'Upper Near Match Threshold is required.';
            tempLow.hasErr = true;
            tempLow.hasErrMsg = 'Lower Near Match Threshold is required.';
          } else if (low === 0) {
            rule.hasErr = true;
            rule.hasErrMsg = 'Upper Near Match Threshold is required.';
            tempLow.hasErr = true;
            tempLow.hasErrMsg = 'Lower Threshold must be between 1 and 100.';
          } else {
            rule.hasErr = true;
            rule.hasErrMsg = 'Upper Near Match Threshold is required.';
            tempLow.hasErr = false;
            tempLow.hasErrMsg = null;
          }
        } else if (high === 0 || high > 100) {
          if (Number.isNaN(low)) {
            rule.hasErr = true;
            rule.hasErrMsg = 'Upper Threshold must be between 1 and 100.';
            tempLow.hasErr = true;
            tempLow.hasErrMsg = 'Lower Near Match Threshold is required.';
          } else if (low === 0) {
            rule.hasErr = true;
            rule.hasErrMsg = 'Upper Threshold must be between 1 and 100.';
            tempLow.hasErr = true;
            tempLow.hasErrMsg = 'Lower Threshold must be between 1 and 100.';
          } else {
            rule.hasErr = true;
            rule.hasErrMsg = 'Upper Threshold must be between 1 and 100.';
            tempLow.hasErr = false;
            tempLow.hasErrMsg = null;
          }
        } else if (high <= low) {
          rule.hasErr = true;
          rule.hasErrMsg = 'Upper Threshold must be more than Lower Threshold.';
          tempLow.hasErr = true;
          tempLow.hasErrMsg = 'Lower Threshold must be less than Upper Threshold.';
        } else if (Number.isNaN(low) && !Number.isNaN(high)) {
          rule.hasErr = false;
          rule.hasErrMsg = null;
          tempLow.hasErr = true;
          tempLow.hasErrMsg = 'Lower Near Match Threshold is required.';
        } else {
          rule.hasErr = false;
          rule.hasErrMsg = null;
          tempLow.hasErr = false;
          tempLow.hasErrMsg = null;
        }
      }
    },
    /* eslint-disable no-param-reassign */
  },
};
</script>

<style lang="sass" scoped>
  table td
    vertical-align: middle
  a.disabled
    color: #999
    cursor: default
    text-decoration: none
    pointer-events: none
    &:hover
      text-decoration: none
  span
    vertical-align: -2px
  .box-error
    border-color: red
  .color-error
    color: red
  .category
    padding-top: 1.5rem
  .header
    display: flex
    align-items: center
  .icon-right
    padding-right: 1rem
  .input-box-width
    width: 200px
  .input-display
    max-width: 25px !important
  .no-border
    border: 0
  .rule
    font-size: 12px
    color: #666
  .th-normal
    font-weight: 400
    vertical-align: middle
</style>
